import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';
import { fetchBillingPage, fetchPaymentAvailability } from '@features/billing/api';
import { BalanceSummary, useAccountAgeing } from '@features/billing/BalanceCard';
import { deriveBalanceState } from '@features/billing/balanceState';
import { useModuleStore } from '@shared/stores/moduleStore';

/**
 * Home widget: the member's account balance (reuses the billing
 * BalanceSummary). Self-gates on the financials module + an existing account.
 */
export function AccountWidget({ config }: WidgetProps): JSX.Element | null {
  const navigate = useNavigate();
  const financialsEnabled = useModuleStore((s) => s.isModuleEnabled('financials'));

  // Same query keys/params as the Invoices page so the cache is shared (deduped).
  const billingQuery = useQuery({
    queryKey: ['billing', 'all'],
    queryFn: () => fetchBillingPage({ type: 'all', limit: 50 }),
    enabled: financialsEnabled,
  });
  const payQuery = useQuery({
    queryKey: ['payment-availability'],
    queryFn: fetchPaymentAvailability,
    enabled: financialsEnabled,
  });
  const ageing = useAccountAgeing();

  if (!financialsEnabled) return null;

  const account = billingQuery.data?.account;
  const state = account ? deriveBalanceState(account, ageing) : null;
  // No billing account linked → hide the widget entirely.
  if (!billingQuery.isLoading && !account) return null;

  return (
    <WidgetCard
      title={widgetTitle(config)}
      actionLabel="View"
      onAction={() => navigate('/app/invoices')}
      onClick={() => navigate('/app/invoices')}
      className={
        state?.status === 'overdue'
          ? 'ring-1 ring-[color-mix(in_oklch,var(--danger)_45%,transparent)]'
          : undefined
      }
    >
      {state && account ? (
        <BalanceSummary state={state} account={account} canPay={payQuery.data === true} />
      ) : (
        <Skeleton className="h-36 w-full rounded-lg" />
      )}
    </WidgetCard>
  );
}
